import { useEffect, useState } from "react";

import { useQuery, useMutation } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { PageHeader } from "@/components/PageHeader";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface NotificationPrefs {
  quoteUpdates: boolean;
  orderStatus: boolean;
  shipmentTracking: boolean;
  invoiceReminders: boolean;
  weeklyDigest: boolean;
}

const ITEMS: { key: keyof NotificationPrefs; title: string; description: string }[] = [
  { key: "quoteUpdates", title: "Sales Quote updates", description: "Get notified when sales quotes are accepted or rejected" },
  { key: "orderStatus", title: "Order status", description: "Receive updates on order status changes" },
  { key: "shipmentTracking", title: "Shipment tracking", description: "Get notified when shipments are updated" },
  { key: "invoiceReminders", title: "Invoice reminders", description: "Receive reminders for unpaid invoices" },
  { key: "weeklyDigest", title: "Weekly digest", description: "Get a weekly summary of your sales activity" },
];

export default function NotificationsPreferencesPage() {
  const { sessionId } = useAuth();

  const { toast } = useToast();
  const [prefs, setPrefs] = useState<NotificationPrefs>({
    quoteUpdates: true, orderStatus: true, shipmentTracking: true,
    invoiceReminders: false, weeklyDigest: false,
  });

  const { data, isLoading } = useQuery<NotificationPrefs>({
    queryKey: ["/api/preferences/notifications"],
    queryFn: () => api.get<NotificationPrefs>(`/preferences/notifications`, sessionId),
  });

  useEffect(() => { if (data) setPrefs(data); }, [data]);

  const saveMutation = useMutation({
    mutationFn: () => api.patch(`/preferences/notifications`, prefs, sessionId),
    onSuccess: () => toast({ title: "Preferences saved" }),
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  if (isLoading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Notifications"
        description="Choose what notifications you receive"
      />

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle className="text-lg font-medium">Notification Preferences</CardTitle>
          <p className="text-sm text-muted-foreground">Configure how you want to be notified</p>
        </CardHeader>
        <CardContent className="space-y-6">
          {ITEMS.map((item) => (
            <div key={item.key} className="flex items-center justify-between">
              <div>
                <div className="font-medium">{item.title}</div>
                <div className="text-sm text-muted-foreground">{item.description}</div>
              </div>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={prefs[item.key]}
                  onChange={(e) => setPrefs((p) => ({ ...p, [item.key]: e.target.checked }))}
                  data-testid={`switch-${item.key}`}
                />
                <div className="w-9 h-5 bg-muted peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-all after:mt-0.5 after:ml-0.5 relative peer-checked:bg-primary" />
              </label>
            </div>
          ))}

          <Separator />

          <div className="flex justify-end">
            <Button
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
              data-testid="button-save-notifications"
            >
              {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Preferences
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
